import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import {
  getAuthenticationOptions,
  verifyAuthentication,
  getRegistrationOptions,
  verifyRegistration,
  deletePasskey,
  renamePasskey,
  getUserPasskeys,
} from "@/api/passkey";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// useUserPasskeys
// ---------------------------------------------------------------------------

/**
 * Fetch all passkeys registered for the currently authenticated user.
 * Used by the PasskeyList component in account settings.
 *
 * Cached for 5 minutes — all mutations (register, rename, delete) invalidate
 * ["user-passkeys"] to force a fresh fetch after any change.
 */
export const useUserPasskeys = () => {
  return useQuery({
    queryKey: ["user-passkeys"],
    queryFn: getUserPasskeys,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// ---------------------------------------------------------------------------
// usePasskeyLogin
// ---------------------------------------------------------------------------

/**
 * Handles the full passkey authentication ceremony:
 *   1. Fetch a challenge from the backend (generateAuthenticationOptions)
 *   2. Prompt the browser WebAuthn API (startAuthentication)
 *   3. Send the signed credential to the backend (verifyAuthentication)
 *   4. On success: invalidate current-user cache, navigate by role
 *
 * Note: verifyAuthentication returns only { id, role }, not the full user
 * object, so we always invalidate ["current-user"] rather than trying to
 * setQueryData — the refetch will populate the cache with the complete shape.
 */
export const usePasskeyLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Step 1 — get the challenge options from the backend
      const options = await getAuthenticationOptions();

      // Step 2 — hand off to the browser (shows biometric / PIN prompt,
      //           or QR code for cross-device flow)
      const credential = await startAuthentication(options);

      // Step 3 — verify the signed response on the backend;
      //           backend sets the JWT cookie on success
      return verifyAuthentication(credential);
    },
    onSuccess: (data) => {
      // Persist minimal user payload for auth guards in this session.
      if (data?.data) {
        queryClient.setQueryData(["current-user"], data.data);
      }

      const role = data?.data?.role;
      if (role === "admin") {
        navigate("/admin/transition");
      } else {
        navigate("/resources");
      }

      toast.success("Login successful", {
        description: "You have been signed in with your passkey.",
        duration: 4000,
      });
    },
    onError: (error) => {
      // NotAllowedError is thrown by the browser when the user dismisses
      // the WebAuthn dialog — treat it as a cancellation, not a failure
      if (error.name === "NotAllowedError") {
        toast.info("Authentication cancelled", {
          description: "The passkey prompt was dismissed.",
          duration: 4000,
        });
        return;
      }

      const status = error.response?.status;
      const message = error.response?.data?.message || error.message || "Login failed";

      console.error("Passkey login failed:", message);

      if (status === 401) {
        toast.error("Passkey not recognised", {
          description: "This passkey could not be verified. It may have been removed.",
          duration: 4000,
        });
      } else {
        toast.error("Login failed", {
          description: message,
          duration: 4000,
        });
      }
    },
  });
};

// ---------------------------------------------------------------------------
// usePasskeyRegister
// ---------------------------------------------------------------------------

/**
 * Handles the full passkey registration ceremony:
 *   1. Fetch registration options from the backend
 *   2. Prompt the browser WebAuthn API (startRegistration)
 *   3. Send the new credential to the backend (verifyRegistration)
 *   4. On success: invalidate ["user-passkeys"] so the list refetches
 */
export const usePasskeyRegister = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ friendlyName }) => {
      // Step 1 — get the challenge and options from the backend
      const options = await getRegistrationOptions(friendlyName);

      // Step 2 — prompt the browser to create a new credential
      const credential = await startRegistration(options);

      // Step 3 — send the new credential to the backend to save
      return verifyRegistration({ credential, friendlyName });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-passkeys"] });

      toast.success("Passkey registered", {
        description: "Your new passkey has been saved successfully.",
        duration: 4000,
      });
    },
    onError: (error) => {
      // InvalidStateError means the authenticator is already registered
      if (error.name === "InvalidStateError") {
        toast.error("Passkey already registered", {
          description: "This authenticator is already linked to your account.",
          duration: 4000,
        });
        return;
      }

      // NotAllowedError — user dismissed the browser prompt
      if (error.name === "NotAllowedError") {
        toast.info("Registration cancelled", {
          description: "The passkey prompt was dismissed.",
          duration: 4000,
        });
        return;
      }

      const message = error.response?.data?.message || error.message || "Registration failed";

      console.error("Passkey registration failed:", message);

      toast.error("Registration failed", {
        description: message,
        duration: 4000,
      });
    },
  });
};

// ---------------------------------------------------------------------------
// useDeletePasskey
// ---------------------------------------------------------------------------

/**
 * Delete a passkey by its database ID.
 * Invalidates ["user-passkeys"] so the PasskeyList refetches after deletion.
 */
export const useDeletePasskey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deletePasskey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-passkeys"] });

      toast.success("Passkey removed", {
        description: "The passkey has been deleted from your account.",
        duration: 4000,
      });
    },
    onError: (error) => {
      const message = error.response?.data?.message || error.message || "Failed to remove passkey";

      console.error("Passkey delete failed:", message);

      toast.error("Failed to remove passkey", {
        description: message,
        duration: 4000,
      });
    },
  });
};

// ---------------------------------------------------------------------------
// useRenamePasskey
// ---------------------------------------------------------------------------

/**
 * Rename a passkey by its database ID.
 * Invalidates ["user-passkeys"] so the PasskeyList refetches with the new name.
 */
export const useRenamePasskey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, friendlyName }) => renamePasskey({ id, friendlyName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-passkeys"] });

      toast.success("Passkey renamed", {
        description: "Your passkey has been updated.",
        duration: 4000,
      });
    },
    onError: (error) => {
      const message = error.response?.data?.message || error.message || "Failed to rename passkey";

      console.error("Passkey rename failed:", message);

      toast.error("Failed to rename passkey", {
        description: message,
        duration: 4000,
      });
    },
  });
};
